"use client"

import Image from "next/image"
import { type ComponentProps, useState } from "react"
import type { VariantProps } from "~/lib/utils"
import { cva, cx } from "~/lib/utils"
import { isBlankLocalFavicon } from "~/lib/favicon-visibility"

const faviconVariants = cva({
  base: "size-9 rounded-[0.375em] bg-white mix-blend-multiply dark:mix-blend-normal",
  variants: {
    contained: {
      true: "p-[0.1875em] border",
    },
  },
  defaultVariants: {
    contained: false,
  },
})

type FaviconProps = Omit<ComponentProps<"img">, "src" | "alt" | "width" | "height"> &
  VariantProps<typeof faviconVariants> & {
    src?: string | null
    size?: number
  }

export const Favicon = (props: FaviconProps) => (
  <FaviconImage key={props.src ?? "missing"} {...props} />
)

function FaviconImage({
  className,
  src,
  title,
  contained,
  size = 64,
  loading = "lazy",
  onError,
  onLoad,
  ...props
}: FaviconProps) {
  const [failed, setFailed] = useState(false)
  if (!src || failed) {
    const initial = Array.from(title?.trim() || "?")[0].toUpperCase()
    return (
      <span
        role="img"
        aria-label={title ? `Icon of ${title}` : "Tool icon"}
        title={title}
        className={cx(
          faviconVariants({ contained, className }),
          "inline-flex shrink-0 items-center justify-center bg-linear-to-br from-primary/15 to-primary/5 font-semibold leading-none text-primary mix-blend-normal",
        )}
      >
        {initial}
      </span>
    )
  }

  return (
    <Image
      src={src}
      alt={title ? `Favicon of ${title}` : "Favicon"}
      loading={loading}
      width={size}
      height={size}
      className={cx(faviconVariants({ contained, className }))}
      unoptimized
      onLoad={event => {
        if (
          event.currentTarget.naturalWidth <= 1 ||
          event.currentTarget.naturalHeight <= 1 ||
          isBlankLocalFavicon(event.currentTarget)
        ) {
          setFailed(true)
        }
        onLoad?.(event)
      }}
      onError={event => {
        setFailed(true)
        onError?.(event)
      }}
      {...props}
    />
  )
}
